import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  TABLE_GENERAL_PARAMETERS_LIST,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import { columnHeader } from "@utils/utils";
import CustomColumnFilter from "@shared/baseUI/CustomColumnFilter/CustomColumnFilter";
import Country from "./components/Country/Country";
import GeneralInfo from "./components/GeneralInfo/GeneralInfo";
import GeneralParameterAction from "./components/GeneralParameterAction/GeneralParameterAction";
import { fetchCountryParameters } from "./GeneralParametersList.api";
import "./GeneralParametersList.scss";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    createNewParameter,
    countries,
  } = props;

  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);
  const [codeOptions, setCodeOptions] = useState([]);

  const sortedCountryList = [...(countries || [])]
    .sort((a, b) => a.label.localeCompare(b.label))
    .filter((country) => country.platform === 1);
  const platformCountryIds = sortedCountryList.map((country) => country.value);

  // Rule 4: distinct codes list - no dedicated endpoint for this,
  // so we fetch every parameter once (no pagination) and derive the distinct codes.
  useEffect(() => {
    if (platformCountryIds.length === 0) return;
    fetchCountryParameters({ countries: platformCountryIds, limit: -1 }).then(
      (response) => {
        const distinctCodes = [
          ...new Set((response.countryParameters || []).map((p) => p.code)),
        ].sort((a, b) => a.localeCompare(b));
        setCodeOptions(distinctCodes.map((code) => ({ value: code, label: code })));
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]);

  let columns = [
    {
      Header: t("CODE"),
      title: "CODE",
      accessor: "code",
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="code" />
      ),
      Filter: (filterProps) => (
        <CustomColumnFilter
          type={FILTER_TYPE_MULTI_SELECT_DROPDOWN}
          label={t("CODE")}
          placeholder={t("SEARCH")}
          name="code"
          labelClass="general-parameters-list__code-label"
          containerClass="general-parameters-list__code-dropdown-container"
          inputClass=""
          defaultOption={[]}
          options={codeOptions}
          // eslint-disable-next-line react/destructuring-assignment
          column={filterProps.column}
        />
      ),
    },
    {
      Header: t("COUNTRY"),
      title: "COUNTRY",
      accessor: "country",
      Cell: ({ cell: { value } }) => <Country data={value} />,
      Filter: (filterProps) => (
        <CustomColumnFilter
          type={FILTER_TYPE_MULTI_SELECT_DROPDOWN}
          label={t("COUNTRY")}
          placeholder={t("SEARCH")}
          name="country"
          labelClass="general-parameters-list__country-label"
          containerClass="general-parameters-list__country-dropdown-container"
          inputClass=""
          defaultOption={[]}
          options={sortedCountryList}
          // eslint-disable-next-line react/destructuring-assignment
          column={filterProps.column}
        />
      ),
    },
    {
      Header: t("VALUE"),
      title: "VALUE",
      accessor: "value",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="value" />
      ),
    },
    {
      Header: t("DESCRIPTION"),
      title: "DESCRIPTION",
      accessor: "description",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="description" />
      ),
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value } }) => (
        <GeneralParameterAction
          data={value}
          updateRefeshStatus={updateRefeshStatus}
          countries={countries}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });

    let country = [];
    let code = [];
    if (appliedFilters !== undefined) {
      const countryFilter = appliedFilters.find(
        (filterItem) => filterItem.column === "country"
      );
      country = countryFilter?.value ?? [];
      const codeFilter = appliedFilters.find(
        (filterItem) => filterItem.column === "code"
      );
      code = codeFilter?.value ?? [];
    }
    if (country.length === 0) {
      country = platformCountryIds;
    }

    setLoading(true);
    fetchCountryParameters({
      search: globalFilter,
      countries: country,
      codes: code,
      page: currentPage,
      limit: pageSize,
      orderColumn: sort ? sort.column : "code",
      orderBy: sort ? sort.dir : "asc",
    })
      .then((response) => {
        const responseData = (response.countryParameters || []).map(
          (parameter) => {
            const tempData = { ...parameter };
            tempData.action = { ...parameter };
            tempData.code = {
              code: tempData.code,
              status: tempData.isActive,
            };
            tempData.country = {
              country: tempData.country,
              status: tempData.isActive,
            };
            tempData.value = {
              value: tempData.value,
              status: tempData.isActive,
            };
            tempData.description = {
              description: tempData.description,
              status: tempData.isActive,
            };
            return tempData;
          }
        );
        setData(responseData);
        setPageCount(response.filteredRecords || 0);
        setLoading(false);
      })
      .catch(() => {
        setData([]);
        setPageCount(0);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  return (
    <EncloseTable
      type={TABLE_GENERAL_PARAMETERS_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={40}
      createNewAction={createNewParameter}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      currentPageToFirst={currentPageToFirst}
      setCurrentPageToFirst={setCurrentPageToFirst}
      loading={loading}
      defaultSortOptions={[{ id: "code", desc: false, dir: "asc" }]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
  columnList: PropTypes.array.isRequired,
  createNewParameter: PropTypes.func,
  countries: PropTypes.arrayOf(PropTypes.object),
};

PrepareDataTable.defaultProps = {
  createNewParameter: null,
  countries: [],
};

export default withTranslation()(PrepareDataTable);
